import { Box, Checkbox, FormControlLabel, List, ListItem, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { logger } from '../../services/logger/logger';
import { ColDef, ISetFilterParams, SetFilterValuesFuncParams } from 'ag-grid-community';
import { debounce, isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button';
import Chips from '../../components/common/Chips';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgDateFilter from '../../components/common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgNumberFilter from '../../components/common/customAgGrid/CustomAgNumberFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import ServerSideAgGrid from '../../components/common/customAgGrid/ServerSideAgGrid';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import SideDrawer from '../../components/common/SideDrawer';
import Typography from '@mui/material/Typography';
import PageHeader from '../../components/PageHeader';
import SelectTypeModal from '../../components/SelectTypeModal';
import {
  useDeleteDeliveryMutation,
  useGetDeliveriesListMutation,
  useGetDeliveryTypesFilterOptionsQuery,
  useGetManagerFilterOptionsQuery,
  useGetPortFilterOptionsQuery,
  useGetSupplierFilterOptionsQuery,
  useGetVesselFilterOptionsQuery,
} from '../../store/apiSlices/delivery/deliveryApis';
import { helpingMethod } from '../../utils/HelpingMethod';
import { canEditRobAndDeliveries } from '../../utils/permissions';
import dayjs from 'dayjs';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { dateRangeFilterPredicate } from '../../utils/agGridPredicates';
import { TruncatedCellText } from '../../components/common/styled/TruncatedCellText';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useTranslation } from 'react-i18next';

// Delivery types data removed from top level - moved inside component to use t function

// ------------------- Types -------------------
interface Delivery {
  id: string | number;
  srno: number;
  vessel: string;
  vesselId?: number;
  manager: string;
  port: string;
  date: string;
  supplier: string;
  lub_costs: number;
  ex_costs: number;
  ex_costs_fifo: number;
  type: string;
  deliveryType?: string; // Raw delivery type (standard, adjusting, secondHand, newBuilding)
  invoiced: string;
  remarks: string;
}

type FilterValues = Record<string, string | number | string[] | number[] | any | null | undefined>;

interface ActionItem {
  label: string;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

interface TableMenuOption {
  label: string;
  onClick: (_rowData?: any) => void;
}

// Local demo header previously used has been removed in favor of reusable component.

// ------------------- Component -------------------
const STORAGE_KEY = 'delivery_grid_state';

const getSavedGridState = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (error) {
    logger.error('Error retrieving saved grid state:', error as Error);
  }
  return null;
};

const DeliveryContainer: React.FC = () => {
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const theme = useTheme();
  const { t } = useTranslation();
  const canAddorEdit = canEditRobAndDeliveries(userInfo?.authorityName);
  const navigate = useNavigate();

  // Restore saved state from sessionStorage
  const savedState = useRef(getSavedGridState());

  const [filterValues, setFilterValues] = useState<FilterValues>(
    savedState.current?.filterValues || {},
  );
  const [debouncedFilterValues, setDebouncedFilterValues] = useState<FilterValues>(
    savedState.current?.filterValues || {},
  );
  const [showDeliveryTypeModal, setShowDeliveryTypeModal] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [typeSelectionError, setTypeSelectionError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(savedState.current?.pageSize || 25);
  const [pageNumber, setPageNumber] = useState<number>(savedState.current?.pageNumber || 0);
  const [sortBy, setSortBy] = useState<string>(savedState.current?.sortBy || '');
  const [sortType, setSortType] = useState<string>(savedState.current?.sortType || '');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<Delivery | null>(null);
  const [resetFiltersSignal, setResetFiltersSignal] = useState<number>(0);
  const [isFilterActive, setIsFilterActive] = useState(savedState.current?.isFilterActive || false);
  const [gridFilterModel, setGridFilterModel] = useState<any>(
    savedState.current?.gridFilterModel || null,
  );

  // Flag to skip the first onFilterChange triggered by grid restoring saved filters
  const isRestoringRef = useRef(!!savedState.current?.gridFilterModel);

  // Persist grid state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          filterValues,
          pageNumber,
          pageSize,
          sortBy,
          sortType,
          isFilterActive,
          gridFilterModel,
        }),
      );
    } catch (error) {
      logger.error('Error saving grid state:', error as Error);
    }
  }, [filterValues, pageNumber, pageSize, sortBy, sortType, isFilterActive, gridFilterModel]);

  // API calls
  const [getDeliveriesList, { data: deliveriesList, isLoading, isError }] =
    useGetDeliveriesListMutation();
  const [deleteDelivery, { isLoading: isDeleting }] = useDeleteDeliveryMutation();

  const { data: managerFilterOptions } = useGetManagerFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: portFilterOptions } = useGetPortFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: supplierFilterOptions } = useGetSupplierFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: vesselFilterOptions } = useGetVesselFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: typesFilterOptions } = useGetDeliveryTypesFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Debounced filter values for API calls
  const debouncedSetFilterValues = useMemo(
    () =>
      debounce((newFilterValues: FilterValues) => {
        setDebouncedFilterValues((prev) =>
          isEqual(prev, newFilterValues) ? prev : newFilterValues,
        );
      }, 1000),
    [],
  );

  // Create API parameters object
  const apiParams = useMemo(
    () => ({
      ...debouncedFilterValues,
      page: pageNumber,
      size: pageSize,
      ...(sortBy && { sortBy, sortDir: sortType }),
    }),
    [debouncedFilterValues, pageNumber, pageSize, sortBy, sortType],
  );

  // Single API call effect - handles both initial load and parameter changes
  const prevApiParamsRef = useRef<any>(null);
  useEffect(() => {
    if (!apiParams) return;
    if (prevApiParamsRef.current && isEqual(prevApiParamsRef.current, apiParams)) {
      return;
    }
    prevApiParamsRef.current = apiParams;
    getDeliveriesList(apiParams);
  }, [apiParams, getDeliveriesList]);

  // Update debounced filter values when filterValues change
  useEffect(() => {
    debouncedSetFilterValues(filterValues);
  }, [filterValues, debouncedSetFilterValues]);

  // Page header action buttons
  const actionBtns: ActionItem[] = canAddorEdit
    ? [
        // {
        //   label: "More Filters",
        //   variant: "outlinedBlue",
        //   onClick: () => setOpenDrawer(true),
        // },
        {
          label: t('deliveries.addDelivery'),
          variant: 'blue',
          onClick: () => setShowDeliveryTypeModal(true),
        },
      ]
    : [];

  const DELIVERY_TYPES = useMemo(
    () => [
      { value: 'standard', label: t('deliveries.standard') },
      { value: 'adjusting', label: t('deliveries.adjusting') },
      { value: 'secondHand', label: t('deliveries.secondHand') },
      { value: 'newBuilding', label: t('deliveries.newBuilding') },
    ],
    [t],
  );

  const vesselFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(vesselFilterOptions) && vesselFilterOptions.length > 0) {
          return params.success(vesselFilterOptions.map((opt: any) => opt.label));
        } else {
          params.success([]);
        }
      },
    }),
    [vesselFilterOptions],
  );

  const managerFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(managerFilterOptions) && managerFilterOptions.length > 0) {
          return params.success(managerFilterOptions.map((opt: any) => opt.label));
        } else {
          params.success([]);
        }
      },
    }),
    [managerFilterOptions],
  );

  const portFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(portFilterOptions) && portFilterOptions.length > 0) {
          return params.success(portFilterOptions.map((opt: any) => opt.label));
        } else {
          params.success([]);
        }
      },
    }),
    [portFilterOptions],
  );

  const supplierFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(supplierFilterOptions) && supplierFilterOptions.length > 0) {
          return params.success(supplierFilterOptions.map((opt: any) => opt.label));
        } else {
          params.success([]);
        }
      },
    }),
    [supplierFilterOptions],
  );

  const typeFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(typesFilterOptions) && typesFilterOptions.length > 0) {
          return params.success(
            typesFilterOptions.map((opt: any) => {
              if (opt === 'Acquisition') return t('deliveries.secondHand');
              return opt;
            }),
          );
        } else {
          params.success([]);
        }
      },
    }),
    [typesFilterOptions, t],
  );

  const invoicedFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: ['Yes', 'No'],
    }),
    [],
  );

  // Delete handlers
  const handleDeleteClick = useCallback((rowData: Delivery) => {
    setDeleteTarget(rowData);
    setShowDeleteConfirm(true);
  }, []);

  // Table row action buttons
  const tableMenuOptions: TableMenuOption[] = useMemo(
    () => [
      {
        label: t('buttons.edit'),
        onClick: (rowData?: Delivery) => {
          if (rowData) {
            // Use rowData directly instead of searching in deliveriesList
            // The rowData already contains all the necessary information from the grid
            navigate(`/deliveries/edit/${rowData.id}`, {
              state: {
                deliveryData: rowData,
                isEdit: true,
              },
            });
          }
        },
      },
      {
        label: t('buttons.delete'),
        onClick: (rowData?: Delivery) => {
          if (rowData) {
            handleDeleteClick(rowData);
          }
        },
      },
    ],
    [navigate, handleDeleteClick, t],
  );

  // Table columns
  const columns: ColDef[] = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'vessel',
        headerName: t('table.columns.vessel'),
        width: 170,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: vesselFilterParams,
      },
      {
        field: 'manager',
        headerName: t('table.columns.manager'),
        width: 170,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: managerFilterParams,
      },
      {
        field: 'port',
        headerName: t('table.columns.port'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: portFilterParams,
      },
      {
        field: 'date',
        headerName: t('table.columns.date'),
        width: 170,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgDateFilter,
        filterParams: {
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          filterOptions: [
            {
              displayKey: 'inRange',
              displayName: t('common.between'),
              numberOfInputs: 2,
              predicate: dateRangeFilterPredicate,
            },
            'equals',
          ],
          defaultOption: 'inRange',
        },
      },
      {
        field: 'supplier',
        headerName: t('table.columns.supplier'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: supplierFilterParams,
      },
      {
        field: 'lub_costs',
        headerName: t('table.columns.lubCosts'),
        width: 150,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgNumberFilter,
        cellRenderer: (params: any) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: theme.palette.inputField.font,
                }}
              >
                {params.value} USD
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'ex_costs',
        headerName: t('table.columns.exCosts'),
        width: 150,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgNumberFilter,
        cellRenderer: (params: any) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: theme.palette.inputField.font,
                }}
              >
                {params.value} USD
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'ex_costs_fifo',
        headerName: t('table.columns.exCostsFifo'),
        width: 180,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgNumberFilter,
        cellRenderer: (params: any) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: theme.palette.inputField.font,
                }}
              >
                {params.value} USD
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: typeFilterParams,
        floatingFilter: true,
        cellRenderer: (params: any) => <Chips width="120px" label={params?.data?.type} />,
      },
      {
        field: 'invoiced',
        headerName: t('table.columns.invoiced'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: invoicedFilterParams,
        cellRenderer: (params: any) => <Chips width="100px" label={params?.data?.invoiced} />,
      },
      {
        field: 'remarks',
        headerName: t('table.columns.remarks'),
        width: 250,
        wrapText: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
        cellRenderer: (params: any) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <TruncatedCellText>{params?.value}</TruncatedCellText>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton menuOptions={tableMenuOptions} rowData={params.data} />
        ),
      },
    ];
  }, [
    managerFilterParams,
    portFilterParams,
    supplierFilterParams,
    vesselFilterParams,
    typeFilterParams,
    invoicedFilterParams,
    tableMenuOptions,
    theme.palette.inputField.font,
    t,
  ]);

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('deliveries.title'), isActive: true, onClick: () => {} },
  ];

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    try {
      const deliveryId = Number(deleteTarget.id) || 0;
      const vesselId = deleteTarget.vesselId || 0;
      const deliveryType = deleteTarget.deliveryType || '';

      const response = await deleteDelivery({
        deliveryId,
        vesselId,
        deliveryType,
      }).unwrap();

      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      showSuccessToast(response?.message);

      // Refresh the list
      getDeliveriesList(apiParams);
    } catch (error: any) {
      logger.error('Failed to delete delivery', error as Error);
    }
  }, [deleteTarget, deleteDelivery, getDeliveriesList, apiParams]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  }, []);

  const onFilterChange = (filterModel: any) => {
    // Skip processing when the grid is restoring saved filters on mount.
    // The saved filterValues are already correctly initialized from sessionStorage,
    // and rebuilding them here would fail because filter option data hasn't loaded yet.
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }

    const hasFilters = !!(filterModel && Object.keys(filterModel).length > 0);

    // Save the raw AG Grid filter model for restoration
    setGridFilterModel(hasFilters ? filterModel : null);

    // If all filters are cleared from the grid UI
    if (!hasFilters) {
      setIsFilterActive(false);
      setFilterValues({});
      setPageNumber(0);
      return;
    }

    setIsFilterActive(true);

    const builtFilters: FilterValues = {};
    if (filterModel?.id) {
      (builtFilters as any)['id'] = Number((filterModel as any)['id']?.filter) || null;
    }

    if (filterModel?.vessel) {
      const selectedLabels = (filterModel?.vessel as any)?.values || [];
      const vesselIds = vesselFilterOptions
        ?.filter((vessel: any) => selectedLabels?.includes(vessel.label))
        ?.map((vessel: any) => vessel.value);
      (builtFilters as any)['vesselIds'] = vesselIds && vesselIds.length > 0 ? vesselIds : null;
    }

    if (filterModel?.manager) {
      const selectedLabels = (filterModel?.manager as any)?.values || [];
      const managerIds = managerFilterOptions
        ?.filter((manager: any) => selectedLabels?.includes(manager.label))
        ?.map((manager: any) => manager.value);
      (builtFilters as any)['managerIds'] = managerIds && managerIds.length > 0 ? managerIds : null;
    }

    if (filterModel?.port) {
      const selectedLabels = (filterModel?.port as any)?.values || [];
      const portIds = portFilterOptions
        ?.filter((port: any) => selectedLabels?.includes(port.label))
        ?.map((port: any) => port.value);
      (builtFilters as any)['portIds'] = portIds && portIds.length > 0 ? portIds : null;
    }

    if (filterModel?.supplier) {
      const selectedLabels = (filterModel?.supplier as any)?.values || [];
      const supplierIds = supplierFilterOptions
        ?.filter((supplier: any) => selectedLabels?.includes(supplier.label))
        ?.map((supplier: any) => supplier.value);
      (builtFilters as any)['supplierIds'] =
        supplierIds && supplierIds.length > 0 ? supplierIds : null;
    }

    if (filterModel?.type) {
      const selectedLabels: string[] = (filterModel?.type as any)?.values || [];
      // Reverse-map UI labels back to original API values
      // "S&P Purchase" in UI → "Acquisition" in typesFilterOptions
      const reverseMappedLabels = selectedLabels.map((label: string) => {
        if (label === 'S&P Purchase') return 'Acquisition';
        return label;
      });
      const types = typesFilterOptions
        ?.filter((type: any) => reverseMappedLabels?.includes(type))
        ?.map((type: any) => {
          if (type === 'Acquisition') return 'ACQUISITION';
          if (type === 'Adjusting') return 'ADJUSTMENT';
          return type?.toUpperCase()?.replace(/\s+/g, '_');
        });
      (builtFilters as any)['types'] = types && types.length > 0 ? types : null;
    }

    if (filterModel?.date != null) {
      if (filterModel.date?.type === 'inRange') {
        const startDate = dayjs((filterModel as any)['date']?.dateFrom).format('YYYY-MM-DD');
        const endDate = dayjs((filterModel as any)['date']?.dateTo).format('YYYY-MM-DD');
        (builtFilters as any)['dateFrom'] = startDate;
        (builtFilters as any)['dateTo'] = endDate;
        (builtFilters as any)['dateSearchType'] = 'BETWEEN';
      } else if (filterModel.date?.type === 'equals') {
        const date = dayjs((filterModel as any)['date']?.dateFrom).format('YYYY-MM-DD');
        (builtFilters as any)['date'] = date ? date : null;
        (builtFilters as any)['dateSearchType'] = 'EQUALS';
      }
    }

    if (filterModel?.remarks) {
      (builtFilters as any)['remarksContains'] = (filterModel as any)['remarks']?.filter || null;
    }

    if (filterModel?.invoiced) {
      const selectedLabels = (filterModel as any)['invoiced']?.values || [];
      const invoicedValues = selectedLabels.map((label: string) => label === 'Yes');
      // If both Yes and No are selected, it's the same as no filter
      (builtFilters as any)['invoiced'] =
        invoicedValues && invoicedValues.length > 0 && invoicedValues.length < 2
          ? invoicedValues
          : null;
    }

    setFilterValues(builtFilters);
    setPageNumber(0);
  };

  const onSortChange = (sortModel: any) => {
    if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0];
      let colId = sort.colId;
      if (colId === 'id') colId = 'id';
      if (colId === 'vessel') colId = 'vesselName';
      if (colId === 'manager') colId = 'managerName';
      if (colId === 'port') colId = 'portName';
      if (colId === 'date') colId = 'deliveryDate';
      if (colId === 'supplier') colId = 'supplierName';
      if (colId === 'type') colId = 'typeOrder';
      if (colId === 'lub_costs') colId = 'totalLubricantCost';
      if (colId === 'ex_costs') colId = 'totalExtraCost';
      if (colId === 'invoiced') colId = 'invoiced';
      if (colId === 'remarks') colId = 'remarks';
      setSortBy(colId);
      setSortType(sort.sort);
      setPageNumber(0);
    } else {
      setSortBy('id');
      setSortType('desc');
      setPageNumber(0);
    }
  };

  // Generate add button label based on selected type
  const getAddButtonLabel = useCallback(
    (type: string): string => {
      if (!type) return t('deliveries.genericAddDelivery');
      const deliveryType = DELIVERY_TYPES.find((t) => t.value === type);
      if (!deliveryType) return t('deliveries.genericAddDelivery');
      return t('deliveries.dynamicAddDelivery', { type: deliveryType.label });
    },
    [DELIVERY_TYPES, t],
  );

  const handleTypeSelection = useCallback((type: string): void => {
    setSelectedType(type);
    setTypeSelectionError(null);
  }, []);

  const handleTypeConfirm = useCallback((): void => {
    setShowDeliveryTypeModal(false);
    navigate('/deliveries/add', { state: { deliveryType: selectedType } });
  }, [navigate, selectedType]);

  const handleCloseModal = useCallback(() => {
    setShowDeliveryTypeModal(false);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(0); // Reset to first page when page size changes
  }, []);

  const handlePageChange = useCallback((newPageNumber: number) => {
    setPageNumber(newPageNumber - 1); // Convert 1-based to 0-based for API
  }, []);

  const handleToggleDrawer = () => {
    setOpenDrawer(!openDrawer);
    setSelectedColumns([]);
    setTableColumns(columns);
  };

  const handleAddColumn = useCallback((column: string) => {
    setSelectedColumns((prev) => {
      const exists = prev.includes(column);
      return exists ? prev.filter((c) => c !== column) : [...prev, column];
    });
  }, []);

  const handleApplyFilter = useCallback(() => {
    const base: ColDef[] = [...columns];
    const vesselIdx = base.findIndex((c) => c.field === 'vessel');
    const insertAt = vesselIdx >= 0 ? vesselIdx + 1 : base.length;

    const existing = new Set(base.map((c) => String(c.field)));
    const newCols: ColDef[] = (selectedColumns || [])
      .filter((f) => !existing.has(f))
      .map((f) => ({
        field: f,
        headerName: helpingMethod.formatChipLabel(f),
        width: 150,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      }));

    const next: ColDef[] = [...base.slice(0, insertAt), ...newCols, ...base.slice(insertAt)];

    setTableColumns(next);
    setOpenDrawer(false);
  }, [columns, selectedColumns]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        resetFilters={() => {
          setIsFilterActive(false);
          setResetFiltersSignal((prev) => (typeof prev === 'number' ? prev + 1 : 1));
          setFilterValues({});
          setPageNumber(0);
          setSortBy('');
          setSortType('');
          setGridFilterModel(null);
          sessionStorage.removeItem(STORAGE_KEY);
        }}
        isFilterActive={isFilterActive}
      />
      <TableCardContent>
        <ServerSideAgGrid
          columnDefs={tableColumns}
          rowData={deliveriesList?.data || []}
          totalRecords={deliveriesList?.pagination || 0}
          isLoading={isLoading}
          isError={isError}
          currentPage={pageNumber + 1}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          resetFiltersSignal={resetFiltersSignal}
          initialFilterModel={savedState.current?.gridFilterModel}
        />
      </TableCardContent>

      <SideDrawer
        open={openDrawer}
        onClose={handleToggleDrawer}
        title={t('buttons.moreFilters')}
        footer={
          <Stack direction={'row'} spacing={1}>
            <Button variant="outlinedBlue" onClick={handleToggleDrawer}>
              {t('buttons.close')}
            </Button>
            <Button variant="blue" onClick={handleApplyFilter}>
              {t('common.apply')}
            </Button>
          </Stack>
        }
        size="xs"
      >
        <Box sx={{ padding: '24px' }}>
          <Typography>{t('common.insertColumns')}</Typography>
          <List>
            {[t('common.vesselId'), t('common.vesselSize')].map((item) => (
              <ListItem key={item} sx={{ cursor: 'pointer' }}>
                <FormControlLabel
                  label={item}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(item)}
                      onChange={() => handleAddColumn(item)}
                    />
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </SideDrawer>

      {showDeliveryTypeModal && (
        <SelectTypeModal
          title={t('deliveries.selectType')}
          typesLabel={t('deliveries.deliveryTypes')}
          open={showDeliveryTypeModal}
          onClose={handleCloseModal}
          selectedType={selectedType}
          onChangeType={handleTypeSelection}
          onConfirm={handleTypeConfirm}
          onCancel={handleCloseModal}
          isSubmitting={false}
          typeOptions={DELIVERY_TYPES}
          confirmButtonLabel={getAddButtonLabel(selectedType)}
          error={typeSelectionError}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          isSubmitting={isDeleting}
          title={t('deliveries.deleteDelivery')}
          description={t('common.deleteConfirmation')}
        />
      )}
    </Stack>
  );
};

export default DeliveryContainer;
